import type { Entry } from "./types";

// Controlled domain enums rendered in the UI. Backend/API values are never
// mutated; these are display-only labels keyed by the raw enum value.
export const enumLabels = {
  role: {
    SUPER_ADMIN: { ko: "슈퍼 관리자", en: "Super Admin" },
    ADMIN: { ko: "컴플라이언스 관리자", en: "Admin" },
    USER: { ko: "요청자", en: "User" },
  },
  membershipStatus: {
    PENDING: { ko: "대기", en: "Pending" },
    ACTIVE: { ko: "활성", en: "Active" },
    DISABLED: { ko: "비활성", en: "Disabled" },
  },
  policySource: {
    REGULATION: { ko: "법령", en: "Regulation" },
    AUTHORITY_GUIDANCE: { ko: "감독기관 가이드", en: "Authority Guidance" },
    INDUSTRY_RULE: { ko: "업계 규정", en: "Industry Rule" },
    INTERNAL_POLICY: { ko: "내부 정책", en: "Internal Policy" },
    PRODUCT_RULE: { ko: "상품 규칙", en: "Product Rule" },
    PROHIBITED_WORDS: { ko: "금지 표현", en: "Prohibited Words" },
    APPROVED_WORDING: { ko: "승인 문구", en: "Approved Wording" },
  },
  approvalStatus: {
    DRAFT: { ko: "초안", en: "Draft" },
    PENDING_APPROVAL: { ko: "승인 대기", en: "Pending Approval" },
    APPROVED: { ko: "승인됨", en: "Approved" },
    RETIRED: { ko: "비활성", en: "Retired" },
  },
  processingStatus: {
    PENDING: { ko: "대기", en: "Pending" },
    PROCESSING: { ko: "처리 중", en: "Processing" },
    READY: { ko: "완료", en: "Ready" },
    FAILED: { ko: "실패", en: "Failed" },
  },
  reviewJobStatus: {
    PENDING: { ko: "대기", en: "Pending" },
    RUNNING: { ko: "분석 중", en: "Running" },
    RETRYING: { ko: "재시도 대기", en: "Retrying" },
    COMPLETED: { ko: "완료", en: "Completed" },
    FAILED: { ko: "실패", en: "Failed" },
  },
  ruleCategory: {
    REQUIRED_DISCLAIMER: { ko: "필수 고지", en: "Required Disclaimer" },
    PROHIBITED_EXPRESSION: { ko: "금지 표현", en: "Prohibited Expression" },
    REQUIRED_FIELD: { ko: "필수 항목", en: "Required Field" },
    COMPARISON_CLAIM: { ko: "비교 표현", en: "Comparison Claim" },
    NUMERIC_CLAIM: { ko: "수치 표현", en: "Numeric Claim" },
    VISUAL_REFERENCE: { ko: "시각 참고 규칙", en: "Visual Reference" },
    GENERAL: { ko: "일반", en: "General" },
  },
  ruleReferenceLabel: {
    POSITIVE: { ko: "금지 사례", en: "Positive / prohibited" },
    NEGATIVE: { ko: "허용 사례", en: "Negative / allowed" },
    BORDERLINE: { ko: "경계 사례", en: "Borderline / review" },
  },
  severity: {
    LOW: { ko: "낮음", en: "Low" },
    MEDIUM: { ko: "보통", en: "Medium" },
    HIGH: { ko: "높음", en: "High" },
    CRITICAL: { ko: "심각", en: "Critical" },
  },
  // Channels are administrator-managed rows, so their names come from the API
  // and are displayed verbatim. Only the guide metadata below is enumerable.
  guideSource: {
    ASSOCIATION_GUIDELINE: { ko: "협회 가이드라인", en: "Association guideline" },
    COMMON_PRACTICE: { ko: "공통 표기 (협회 가이드 외)", en: "Common practice" },
    OTHER: { ko: "기타", en: "Other" },
  },
  wordingType: {
    FIXED: { ko: "고정문구", en: "Fixed wording" },
    PER_PRODUCT: { ko: "상품별 문구", en: "Per-product wording" },
  },
  forbiddenCategory: {
    EXAGGERATION: { ko: "과장과 단정적 표현", en: "Exaggerated or absolute claim" },
    MISLEADING: { ko: "오인유도", en: "Misleading" },
    ONE_SIDED: { ko: "편면적 정보제공", en: "One-sided information" },
    PROVOCATIVE: { ko: "적합안내와 자극적 표현", en: "Provocative" },
    DISPARAGEMENT: { ko: "비방", en: "Disparagement" },
    PROHIBITED_WORDING: { ko: "금지문언", en: "Prohibited wording" },
    SIGNUP_INDUCEMENT: { ko: "가입시점 유인", en: "Signup inducement" },
    TECHNICAL_TERM: { ko: "전문용어", en: "Technical term" },
    INCORRECT_SOURCE: { ko: "잘못된 자료표기", en: "Incorrect source" },
    CUSTOMER_INDUCEMENT: { ko: "고객유인", en: "Customer inducement" },
    OTHER: { ko: "기타", en: "Other" },
  },
  forbiddenMatchType: {
    SUBSTRING: { ko: "문구 포함", en: "Contains text" },
    REGEX: { ko: "정규식", en: "Regular expression" },
  },
  reviewStatus: {
    DRAFT: { ko: "초안", en: "Draft" },
    SUBMITTED: { ko: "제출됨", en: "Submitted" },
    ANALYZING: { ko: "분석 중", en: "Analyzing" },
    AWAITING_DECISION: { ko: "결정 대기", en: "Awaiting Decision" },
    SELF_CHECK_COMPLETE: { ko: "사전 검토 완료", en: "Self-check Complete" },
    SELF_CHECK_REJECTED: { ko: "사전 검토 반려", en: "Self-check Rejected" },
    DECIDED: { ko: "결정 완료", en: "Decided" },
  },
  riskStatus: {
    PASS: { ko: "통과", en: "Pass" },
    REVIEW_REQUIRED: { ko: "검토 필요", en: "Review Required" },
    HIGH_RISK: { ko: "고위험", en: "High Risk" },
  },
  decision: {
    APPROVED: { ko: "승인", en: "Approved" },
    APPROVED_WITH_CHANGES: { ko: "조건부 승인", en: "Approved with Changes" },
    REJECTED: { ko: "반려", en: "Rejected" },
  },
  citationType: {
    RULE: { ko: "규칙", en: "Rule" },
    POLICY: { ko: "정책", en: "Policy" },
    BASELINE: { ko: "기본 참고 기준", en: "Baseline reference" },
    DICTIONARY: { ko: "금지 표현 사전", en: "Forbidden expression dictionary" },
    NONE: { ko: "근거 미등록", en: "No registered source" },
  },
  checkVerdict: {
    OK: { ko: "문제 없음", en: "No issue" },
    ISSUE: { ko: "지적 있음", en: "Issue found" },
    NOT_APPLICABLE: { ko: "해당 없음", en: "Not applicable" },
    CANNOT_VERIFY: { ko: "확인 불가", en: "Cannot verify" },
  },
  evidenceType: {
    TEXT: { ko: "문구 근거", en: "Text evidence" },
    VISUAL: { ko: "이미지 근거", en: "Visual evidence" },
  },
  detectionMethod: {
    DETERMINISTIC: { ko: "규칙 기반", en: "Deterministic" },
    AI: { ko: "AI", en: "AI" },
  },
  confidenceLevel: {
    LOW: { ko: "낮음", en: "Low" },
    MEDIUM: { ko: "보통", en: "Medium" },
    HIGH: { ko: "높음", en: "High" },
  },
  findingDisposition: {
    CONFIRMED: { ko: "인정", en: "Confirmed" },
    FALSE_FLAG: { ko: "오탐", en: "False flag" },
    NOT_APPLICABLE: { ko: "해당 없음", en: "Not applicable" },
  },
  autoClearReadiness: {
    INSUFFICIENT_DATA: { ko: "근거 부족", en: "Insufficient data" },
    NOT_READY: { ko: "기준 미달", en: "Not ready" },
    READY: { ko: "승격 가능", en: "Ready" },
    ACTIVE: { ko: "적용 중", en: "Active" },
    SUSPENDED: { ko: "중단 필요", en: "Suspended" },
  },
  analysisStatus: {
    PENDING: { ko: "대기", en: "Pending" },
    RUNNING: { ko: "실행 중", en: "Running" },
    COMPLETED: { ko: "완료", en: "Completed" },
    FAILED: { ko: "실패", en: "Failed" },
  },
  supportTicketKind: {
    SYSTEM_ERROR: { ko: "시스템 오류", en: "System problem" },
    DISPUTED_RESULT: { ko: "검토 결과 이의", en: "Disputed result" },
    QUESTION: { ko: "사용 문의", en: "Question" },
  },
  // List-row abbreviations: the full label stays on the tooltip.
  supportTicketKindShort: {
    SYSTEM_ERROR: { ko: "오류", en: "Error" },
    DISPUTED_RESULT: { ko: "이의", en: "Dispute" },
    QUESTION: { ko: "문의", en: "Q" },
  },
  supportTicketStatus: {
    OPEN: { ko: "접수", en: "Open" },
    ACKNOWLEDGED: { ko: "확인함", en: "Acknowledged" },
    IN_PROGRESS: { ko: "처리 중", en: "In progress" },
    RESOLVED: { ko: "해결됨", en: "Resolved" },
    WONT_FIX: { ko: "조치 없음", en: "Won't fix" },
  },
  supportSeverity: {
    BLOCKING: { ko: "업무 중단", en: "Blocking my work" },
    DEGRADED: { ko: "불편함", en: "Working around it" },
    MINOR: { ko: "경미함", en: "Minor" },
  },
  supportReportAnchor: {
    REVIEW: { ko: "검토 전체", en: "The review" },
    AI_SUMMARY: { ko: "AI 요약", en: "AI summary" },
    CHECKLIST_ITEM: { ko: "점검 항목", en: "Checklist item" },
    FINDING: { ko: "지적 사항", en: "Finding" },
    ANALYSIS_FAILURE: { ko: "분석 실패", en: "Analysis failure" },
  },
  supportReportReason: {
    NOT_A_VIOLATION: { ko: "위반이 아닙니다", en: "Not a violation" },
    WRONG_CITATION: { ko: "근거가 잘못되었습니다", en: "Wrong citation" },
    WRONG_SEVERITY: { ko: "심각도가 잘못되었습니다", en: "Wrong severity" },
    MISSED_SOMETHING: { ko: "놓친 부분이 있습니다", en: "It missed something" },
    EXPLANATION_UNCLEAR: { ko: "설명이 이해되지 않습니다", en: "Explanation is unclear" },
    ANALYSIS_FAILED: { ko: "분석이 실패했습니다", en: "The analysis failed" },
    OTHER: { ko: "기타", en: "Something else" },
  },
  supportReportReasonShort: {
    NOT_A_VIOLATION: { ko: "비위반", en: "Not it" },
    WRONG_CITATION: { ko: "근거", en: "Basis" },
    WRONG_SEVERITY: { ko: "등급", en: "Sev" },
    MISSED_SOMETHING: { ko: "누락", en: "Miss" },
    EXPLANATION_UNCLEAR: { ko: "불명", en: "Unclear" },
    ANALYSIS_FAILED: { ko: "실패", en: "Fail" },
    OTHER: { ko: "기타", en: "Other" },
  },
  supportTicketSort: {
    PRIORITY: { ko: "우선순위 (중단 먼저)", en: "Priority (blocking first)" },
    RECENTLY_UPDATED: { ko: "최근 업데이트 순", en: "Recently updated" },
    NEWEST: { ko: "최신 접수 순", en: "Newest first" },
    OLDEST: { ko: "오래된 접수 순", en: "Oldest first" },
    SEVERITY: { ko: "영향도 순", en: "Impact" },
    STATUS: { ko: "상태 순", en: "Status" },
  },
  supportAuthorKind: {
    REPORTER: { ko: "신고자", en: "Reporter" },
    SUPPORT: { ko: "지원팀", en: "Support" },
    SYSTEM: { ko: "시스템", en: "System" },
  },
  auditAction: {
    ORGANIZATION_CREATED: { ko: "조직 생성", en: "Organization Created" },
    ORGANIZATION_UPDATED: { ko: "조직 수정", en: "Organization Updated" },
    ORGANIZATION_ARCHIVED: { ko: "조직 삭제", en: "Organization Deleted" },
    VAULTSAGE_ORPHAN_STORAGE_DELETED: {
      ko: "고아 VaultSage 폴더 삭제",
      en: "Orphan VaultSage Folder Deleted",
    },
    ORGANIZATION_SELECTED: { ko: "조직 선택", en: "Organization Selected" },
    LOGIN: { ko: "로그인", en: "Login" },
    LOGOUT: { ko: "로그아웃", en: "Logout" },
    USER_PROFILE_UPDATED: { ko: "사용자 프로필 수정", en: "User Profile Updated" },
    MEMBER_INVITED: { ko: "구성원 초대", en: "Member Invited" },
    MEMBER_ACTIVATED: { ko: "구성원 활성화", en: "Member Activated" },
    MEMBER_NAME_UPDATED: { ko: "구성원 이름 수정", en: "Member Name Updated" },
    MEMBER_DEPARTMENT_UPDATED: { ko: "구성원 소속 수정", en: "Member Department Updated" },
    MEMBER_ROLE_CHANGED: { ko: "역할 변경", en: "Member Role Changed" },
    MEMBER_DISABLED: { ko: "구성원 비활성화", en: "Member Disabled" },
    MEMBER_DELETED: { ko: "구성원 삭제", en: "Member Deleted" },
    POLICY_CREATED: { ko: "정책 생성", en: "Policy Created" },
    POLICY_VERSION_UPLOADED: { ko: "정책 버전 업로드", en: "Policy Version Uploaded" },
    POLICY_ACTIVATED: { ko: "정책 활성화", en: "Policy Activated" },
    POLICY_RETIRED: { ko: "정책 비활성화", en: "Policy Retired" },
    POLICY_UNARCHIVED: { ko: "정책 보관 해제", en: "Policy Unarchived" },
    POLICY_ARCHIVED: { ko: "정책 보관", en: "Policy Archived" },
    POLICY_DELETED: { ko: "정책 삭제", en: "Policy Deleted" },
    POLICY_VERSION_DOWNLOADED: { ko: "정책 문서 다운로드", en: "Policy Document Downloaded" },
    POLICY_VERSION_REPROCESSED: { ko: "정책 문서 색인 재시도", en: "Policy Document Reindexed" },
    POLICY_SCOPE_UPDATED: { ko: "정책 적용 범위 수정", en: "Policy Scope Updated" },
    RULE_CREATED: { ko: "규칙 생성", en: "Rule Created" },
    RULE_VERSION_CREATED: { ko: "규칙 버전 생성", en: "Rule Version Created" },
    RULE_ACTIVATED: { ko: "규칙 활성화", en: "Rule Activated" },
    RULE_RETIRED: { ko: "규칙 비활성화", en: "Rule Retired" },
    RULE_DELETED: { ko: "규칙 삭제", en: "Rule Deleted" },
    RULE_REFERENCE_UPLOADED: { ko: "규칙 참고 이미지 업로드", en: "Rule Reference Uploaded" },
    RULE_REFERENCE_DELETED: { ko: "규칙 참고 이미지 삭제", en: "Rule Reference Deleted" },
    RULE_CHANNEL_UPDATED: { ko: "규칙 채널 매핑 변경", en: "Rule Channel Mapping Updated" },
    RULE_SCOPE_UPDATED: { ko: "규칙 적용 범위 수정", en: "Rule Scope Updated" },
    CHANNEL_CREATED: { ko: "채널 생성", en: "Channel Created" },
    CHANNEL_UPDATED: { ko: "채널 수정", en: "Channel Updated" },
    CHANNEL_DELETED: { ko: "채널 삭제", en: "Channel Deleted" },
    CATEGORY_CREATED: { ko: "자료 구분 생성", en: "Category Created" },
    CATEGORY_UPDATED: { ko: "자료 구분 수정", en: "Category Updated" },
    CATEGORY_DELETED: { ko: "자료 구분 삭제", en: "Category Deleted" },
    CATEGORY_TEMPLATE_APPLIED: { ko: "자료 구분 템플릿 적용", en: "Category Template Applied" },
    DICTIONARY_ENTRY_CREATED: { ko: "금지 표현 추가", en: "Dictionary Entry Created" },
    DICTIONARY_ENTRY_UPDATED: { ko: "금지 표현 수정", en: "Dictionary Entry Updated" },
    DICTIONARY_ENTRY_DELETED: { ko: "금지 표현 삭제", en: "Dictionary Entry Deleted" },
    DICTIONARY_IMPORTED: { ko: "금지 표현 일괄 등록", en: "Dictionary Imported" },
    SYNONYM_GROUP_CREATED: { ko: "유사어 추가", en: "Synonym Group Created" },
    SYNONYM_GROUP_UPDATED: { ko: "유사어 수정", en: "Synonym Group Updated" },
    SYNONYM_GROUP_DELETED: { ko: "유사어 삭제", en: "Synonym Group Deleted" },
    BASELINE_GUIDE_APPLIED: { ko: "기본 가이드 적용", en: "Baseline Guide Applied" },
    REVIEW_CREATED: { ko: "검토 생성", en: "Review Created" },
    REVIEW_SUBMITTED: { ko: "검토 제출", en: "Review Submitted" },
    REVIEW_ANALYZED: { ko: "검토 분석", en: "Review Analyzed" },
    REVIEW_RESUBMITTED: { ko: "검토 재제출", en: "Review Resubmitted" },
    REVIEW_RERUN: { ko: "최신 설정으로 재검토", en: "Review Re-run" },
    REVIEW_ASSIGNED: { ko: "검토 할당", en: "Review Assigned" },
    FINDING_NOTE_UPDATED: { ko: "지적 사항 메모 수정", en: "Finding Note Updated" },
    FINDING_LOCATION_UPDATED: { ko: "지적 위치 수정", en: "Finding Location Updated" },
    DECISION_RECORDED: { ko: "결정 기록", en: "Decision Recorded" },
    REPORT_DOWNLOADED: { ko: "보고서 다운로드", en: "Report Downloaded" },
    APPROVED_DOCUMENT_DOWNLOADED: {
      ko: "승인 문서 다운로드",
      en: "Approved Document Downloaded",
    },
    SUPPORT_TICKET_CREATED: { ko: "문의 접수", en: "Support Ticket Created" },
    SUPPORT_TICKET_STATUS_CHANGED: { ko: "문의 상태 변경", en: "Support Ticket Status Changed" },
    SUPPORT_TICKET_REPLIED: { ko: "문의 답변", en: "Support Ticket Replied" },
    SUPPORT_TICKET_INSPECTED: {
      ko: "문의 관련 자료 열람",
      en: "Support Ticket Material Inspected",
    },
    SUPPORT_TICKET_MATERIAL_VIEWED: {
      ko: "문의 관련 원본 파일 열람",
      en: "Support Ticket File Opened",
    },
    LLM_SETTINGS_UPDATED: { ko: "LLM 설정 변경", en: "LLM Settings Updated" },
  },
} as const satisfies Record<string, Record<string, Entry>>;

export type EnumKind = keyof typeof enumLabels;
